/**
 * Carga un módulo lazy sobreviviendo a un despliegue hecho con la pestaña abierta.
 *
 * ── QUÉ ROMPE, Y POR QUÉ NO SE ARREGLA SOLO ─────────────────────────────────
 *
 * Vite firma cada chunk con un hash (`inventory.router-DB0Qenxj.js`). Al desplegar, los archivos
 * viejos DESAPARECEN del servidor, pero la pestaña que ya estaba abierta sigue corriendo el
 * `index.html` anterior, que los nombra. La primera navegación a una ruta lazy pide un archivo
 * que ya no existe: 404, promesa rechazada sin capturar, y la pantalla queda rota.
 *
 * No es hipotético: le pasó a un usuario el 2026-08-07 a los cuatro minutos de un despliegue,
 * entrando a la orden de producción de un expediente. Y no era la primera vez — otra ruta
 * acumulaba 10 eventos en 4 días. Cada despliegue le rompe la sesión a quien tenga la app
 * abierta, y esa persona ve una pantalla muerta sin manera de saber que alcanza con recargar.
 *
 * ── LA CURA, Y POR QUÉ NO PUEDE SER SÓLO REINTENTAR ─────────────────────────
 *
 * Reintentar el mismo import NO trae de vuelta un archivo borrado: esa URL no va a existir nunca
 * más. Lo único que lo cura es **recargar**, porque eso vuelve a pedir el `index.html` y con él
 * los hashes nuevos.
 *
 * Aun así se reintenta UNA vez antes de recargar, y es a propósito: un bache de red momentáneo
 * produce el mismo error, y para ese caso tirarle una recarga completa al usuario es una
 * respuesta desproporcionada. El reintento cubre el bache; la recarga cubre el despliegue.
 *
 * ── POR QUÉ NO ENTRA EN BUCLE ───────────────────────────────────────────────
 *
 * La recarga se anota en `sessionStorage`, y con la anotación puesta NO se vuelve a recargar: si
 * el módulo sigue fallando después de recargar, el problema no era el despliegue y hay que
 * dejarlo estallar para que se vea. La marca se borra en cuanto una carga sale bien, así que un
 * segundo despliegue más tarde en la misma sesión vuelve a tener su recarga disponible. Sin
 * borrarla, el usuario gastaría el presupuesto una vez y no se recuperaría nunca más.
 */

const RELOAD_MARK = 'idc:chunk-reload'

/**
 * `sessionStorage` tira en modo privado de algunos navegadores y dentro de un iframe sin
 * permisos. Si no se puede leer, se asume que NO se recargó: es preferible una recarga de más
 * —que en el peor caso el usuario ve como un parpadeo— a dejarlo con la pantalla rota.
 */
const alreadyReloaded = (): boolean => {
  try {
    return window.sessionStorage.getItem(RELOAD_MARK) !== null
  } catch {
    return false
  }
}

const markReloaded = (): void => {
  try {
    window.sessionStorage.setItem(RELOAD_MARK, String(Date.now()))
  } catch {
    // Sin poder anotar no hay presupuesto que llevar. Se recarga igual: el bucle lo corta el
    // navegador al servir el `index.html` nuevo, que es justamente lo que arregla el problema.
  }
}

const clearReloadMark = (): void => {
  try {
    window.sessionStorage.removeItem(RELOAD_MARK)
  } catch {
    // Nada que hacer: la marca es una optimización, no un requisito de correctitud.
  }
}

export async function retryImport<T>(load: () => Promise<T>): Promise<T> {
  try {
    const loaded = await load()
    clearReloadMark()
    return loaded
  } catch (error) {
    try {
      const loaded = await load()
      clearReloadMark()
      return loaded
    } catch {
      // El segundo intento tampoco: o el chunk ya no existe, o la red está caída.
    }

    if (alreadyReloaded()) throw error

    markReloaded()
    window.location.reload()

    /*
     * Una promesa que nunca resuelve, y es deliberado: `reload()` no corta la ejecución, así que
     * sin esto React seguiría adelante y pintaría el error un instante antes de que la página se
     * vaya. Ese parpadeo es exactamente lo que se está viniendo a sacar.
     */
    return new Promise<T>(() => {})
  }
}
